import { NgClass } from '@angular/common'
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  Injector,
  OnInit,
  Signal,
  computed,
  effect,
  inject,
  input,
  runInInjectionContext,
} from '@angular/core'

import { FlxStyleModule, FlxThemeModule, FlxWrapperStyleDirective } from '@flowx/angular-theme'
import { FlxIconModule } from '@flowx/angular-ui-toolkit'
import { BadgeTemplateConfig } from '@flowx/core-sdk'

import { getBoolExpressionSignal, getContentIcon, getDynamicPropSignal } from '../helpers'

@Component({
  selector: 'flx-badge-wrapper',
  hostDirectives: [
    { directive: FlxWrapperStyleDirective, inputs: ['flxWrapperStyle: templateConfig'] },
  ],
  imports: [NgClass, FlxThemeModule, FlxStyleModule, FlxIconModule],
  templateUrl: './badge-wrapper.component.html',
  styleUrl: './badge-wrapper.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlxBadgeWrapperComponent implements OnInit, AfterViewInit {
  readonly #injector = inject(Injector)
  readonly #flxWrapperStyle = inject(FlxWrapperStyleDirective)

  templateConfig = input.required<BadgeTemplateConfig>()

  displayOptions = computed(() => this.templateConfig().displayOptions)
  flowxProps = computed(() => this.displayOptions().flowxProps)

  // expressions
  isHidden: Signal<boolean>
  // dynamic props
  label: Signal<string>

  readonly iconPath = computed(() => {
    const icon = this.flowxProps()?.icon
    if (!icon?.value) {
      return null
    }
    return getContentIcon(icon)
  })

  readonly variantClass = computed(() => `badge--${this.flowxProps()?.variant || 'neutral'}`)
  readonly hostClass = computed(() =>
    [this.displayOptions().className, this.variantClass()].filter(Boolean).join(' ')
  )

  ngOnInit(): void {
    runInInjectionContext(this.#injector, () => {
      const props = this.flowxProps()
      this.isHidden = getBoolExpressionSignal(props.hide, this.templateConfig())
      this.label = getDynamicPropSignal(props.label || '', this.templateConfig())
    })
  }

  ngAfterViewInit(): void {
    runInInjectionContext(this.#injector, () => {
      effect(() => {
        this.#flxWrapperStyle.toggleDisplay(this.isHidden())
      })
    })
  }
}
